"use client";

import { PositionCard, type Position } from "./PositionCard";

interface PositionListProps {
  positions: Position[];
  currentPrices: Record<string, number>;
  onClosePosition?: (positionId: string) => void;
  onEditPosition?: (positionId: string) => void;
  isLoading?: boolean;
}

export const PositionList = ({
  positions,
  currentPrices,
  onClosePosition,
  onEditPosition,
  isLoading = false,
}: PositionListProps) => {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <div key={i} className="h-48 animate-pulse rounded-xl bg-slate-700/50" />
        ))}
      </div>
    );
  }

  if (positions.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200/60 bg-white/5 p-8 text-center">
        <p className="text-sm text-slate-500">No open positions</p>
        <p className="mt-1 text-xs text-slate-600">
          Open a position using the order entry panel
        </p>
      </div>
    );
  }

  // Calculate total PnL
  const totalPnl = positions.reduce((sum, p) => sum + p.unrealizedPnl, 0);
  const totalMargin = positions.reduce((sum, p) => sum + p.margin, 0);

  return (
    <div className="space-y-4">
      {/* Summary Bar */}
      <div className="flex items-center justify-between rounded-lg bg-slate-800/50 px-4 py-2 text-xs">
        <div>
          <span className="text-slate-500">Positions: </span>
          <span className="text-white">{positions.length}</span>
        </div>
        <div>
          <span className="text-slate-500">Total Margin: </span>
          <span className="text-white">${totalMargin.toFixed(2)}</span>
        </div>
        <div>
          <span className="text-slate-500">Total PnL: </span>
          <span className={totalPnl >= 0 ? "text-emerald-400" : "text-rose-400"}>
            {totalPnl >= 0 ? "+" : ""}${totalPnl.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Position Cards */}
      <div className="space-y-3">
        {positions.map((position) => (
          <PositionCard
            key={position.id}
            position={position}
            currentPrice={currentPrices[position.asset] || position.entryPrice}
            onClose={onClosePosition}
            onEdit={onEditPosition}
          />
        ))}
      </div>
    </div>
  );
};
